import { Download, File as FileIcon, FileText, Image as ImageIcon, Loader2, UploadCloud } from "lucide-react";
import { useContext, useRef, useState } from "react";
import { toast } from "sonner";
import { ApplicantDocument } from "../../lib/interfaces/applicant";
import { cn } from "../../lib/utils/cn";
import { Context } from "../root/context";

const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

const documentIcon = (mimeType?: string) => {
  if (mimeType?.includes("pdf")) return FileText;
  if (mimeType?.startsWith("image/")) return ImageIcon;
  return FileIcon;
};

const buildUploadUrl = (applicantId: string) => {
  const base = process.env.REACT_APP_API;
  const prefix = base?.includes("localhost") ? "v1.0" : "api/v1.0";
  return `${base}/${prefix}/applicants/${applicantId}/documents`;
};

const ApplicantDocuments = ({
  applicantId,
  initialDocuments,
  canUpload,
}: {
  applicantId: string;
  initialDocuments?: ApplicantDocument[];
  canUpload: boolean;
}) => {
  const { accessToken } = useContext(Context);
  const [documents, setDocuments] = useState<ApplicantDocument[]>(initialDocuments ?? []);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (file: File) => {
    if (file.size > MAX_SIZE) {
      toast.error(`"${file.name}" ist größer als 10 MB.`);
      return;
    }
    setIsUploading(true);
    const form = new FormData();
    form.append("file", file); // Content-Type/boundary wird vom Browser gesetzt.
    try {
      const response = await fetch(buildUploadUrl(applicantId), {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: form,
      });
      if (!response.ok) {
        throw new Error(response.status === 413 ? "Datei zu groß" : "Upload fehlgeschlagen");
      }
      const updated = (await response.json()) as ApplicantDocument[];
      setDocuments(updated);
      toast.success("Dokument hochgeladen.");
    } catch {
      toast.error("Der Upload ist fehlgeschlagen. Bitte versuche es erneut.");
    } finally {
      setIsUploading(false);
    }
  };

  const uploadFiles = async (files: FileList | null) => {
    if (!files) return;
    for (const file of Array.from(files)) {
      // sequentiell — jede Response enthält die vollständige, aktualisierte Liste
      await uploadFile(file);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-5 flex items-center justify-between gap-3">
        <h2 className="text-base font-bold text-slate-900">Dokumente</h2>
        <span className="text-sm text-slate-500">
          {documents.length} {documents.length === 1 ? "Dokument" : "Dokumente"}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {documents.map((doc, index) => {
          const Icon = documentIcon(doc.mimeType);
          const label = doc.fileName || doc.caption || "Dokument";
          return (
            <div key={`${label}-${index}`} className="flex flex-col gap-3 rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 flex-none place-items-center rounded-lg bg-[#e6f5fb]">
                  <Icon className="h-5 w-5 text-[#0b6f95]" />
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-900" title={label}>
                    {label}
                  </div>
                  <div className="text-xs text-slate-400">
                    {doc.source === "whatsAppAi" ? "WhatsApp KI" : "Manuell"}
                  </div>
                </div>
              </div>
              <a
                href={doc.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-slate-300 hover:text-primary"
              >
                <Download className="h-4 w-4" />
                Öffnen
              </a>
            </div>
          );
        })}

        {canUpload && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              uploadFiles(e.dataTransfer.files);
            }}
            disabled={isUploading}
            className={cn(
              "flex min-h-[118px] flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed px-4 py-5 text-center transition-colors",
              isDragging ? "border-[#5257d6] bg-[#f5efff]" : "border-[#cdb8ec] bg-[#faf7ff] hover:bg-[#f5efff]",
              isUploading && "cursor-not-allowed opacity-70",
            )}
          >
            {isUploading ? (
              <Loader2 className="h-5 w-5 animate-spin text-[#5257d6]" />
            ) : (
              <UploadCloud className="h-5 w-5 text-[#5257d6]" />
            )}
            <span className="text-sm font-semibold text-[#5257d6]">
              {isUploading ? "Wird hochgeladen…" : "Datei hierher ziehen"}
            </span>
            <span className="text-xs text-[#9a8fc0]">oder klicken · max. 10 MB</span>
          </button>
        )}
      </div>

      {documents.length === 0 && !canUpload && (
        <p className="mt-2 text-sm text-slate-400">Keine Dokumente vorhanden.</p>
      )}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          uploadFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </section>
  );
};

export default ApplicantDocuments;
